import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { emailConnectionService } from "@/services/email-connection";

import type {
  CreateEmailConnectionReq,
  UpdateEmailConnectionReq,
} from "@artisancode/api-types";

export function useEmailConnections() {
  return useQuery({
    queryKey: queryKeys.emailConnections.all,
    queryFn: emailConnectionService.list,
  });
}

function useInvalidateEmailConnections() {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({ queryKey: queryKeys.emailConnections.all });
}

export function useCreateEmailConnection() {
  const invalidate = useInvalidateEmailConnections();
  return useMutation({
    mutationFn: (data: CreateEmailConnectionReq) =>
      emailConnectionService.create(data),
    onSuccess: invalidate,
  });
}

export function useUpdateEmailConnection() {
  const invalidate = useInvalidateEmailConnections();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateEmailConnectionReq & { id: string }) =>
      emailConnectionService.update(id, data),
    onSuccess: invalidate,
  });
}

export function useRemoveEmailConnection() {
  const invalidate = useInvalidateEmailConnections();
  return useMutation({
    mutationFn: (id: string) => emailConnectionService.remove(id),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryEmailConnection() {
  const invalidate = useInvalidateEmailConnections();
  return useMutation({
    mutationFn: (id: string) => emailConnectionService.setPrimary(id),
    onSuccess: invalidate,
  });
}
